import { Category, Meta, fetchCategory } from 'common/apiService';
import ContentList from 'common/ContentList';
import Layout from 'common/Layout/Layout';
import { Text } from 'common/styles';
import usePagination from 'common/usePagination';
import WidgetManager from 'common/Components/Widgets/WidgetManager';
import InfiniteScroll from 'common/InfiniteScroll';
import { __t } from 'common/i18n';
import ContentListLoading from 'common/ContentListLoading';
import { ThemeProvider } from '@emotion/react';
import paletteDark from 'common/theme/paletteDark';
import xIPCountry from '../../common/apiService/xIPCountry';
import * as Sentry from '@sentry/nextjs';
import Seo, { SITE_URL } from 'common/Seo';
import Breadcrumb from 'common/Breadcrumb';
import Head from 'next/head';
import { safeJsonLd } from 'common/utils/safeJsonLd';
import CompactHeader, { canShowCompactHeader } from 'common/Components/CompactHeader';

type CategorySinglePageProps = {
  category: Category;
  meta: Meta;
};

export default function CategorySinglePage(props: CategorySinglePageProps) {
  const {
    category: { title, body, widgets, short_id, slug, type, videos, compact_header, show_header_details },
    meta,
  } = props;

  // `videos` is the first page; `meta.next_url` loads the rest, already filtered for the category's type.
  const initialData = {
    data: videos || [],
    meta,
  };
  const { data: contents, getInfiniteScrollProps, hasMore } = usePagination({ initialData });

  const description =
    type === 'genre' ? `تماشای آنلاین فیلم و سریال‌های ژانر ${title} در فیلمنت` : `تماشای آنلاین فیلم و سریال‌های ${title} در فیلمنت`;

  // In compact mode the title/body sit on the first widget's cover.
  const compact = compact_header && canShowCompactHeader(widgets);

  const breadcrumbs = [
    { name: __t('common.home'), url: '/' },
    { name: __t('common.categories'), url: '/categories' },
    { name: title, url: `/categories/${short_id}/${slug}` },
  ];

  return (
    <ThemeProvider theme={{ palette: paletteDark }}>
      <Layout disableFooter={hasMore}>
        <Seo title={`${title} | ${__t('static_siteName')}`} description={description} path={`/categories/${short_id}/${slug}`} />
        <Breadcrumb items={breadcrumbs} />
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: safeJsonLd({
                '@context': 'https://schema.org',
                '@type': 'CollectionPage',
                name: `${title} | ${__t('static_siteName')}`,
                description,
                url: `${SITE_URL}/categories/${short_id}/${slug}`,
                isPartOf: { '@type': 'WebSite', '@id': `${SITE_URL}/#website` },
              }),
            }}
          />
        </Head>
        {show_header_details &&
          (compact ? (
            <CompactHeader title={title} body={body} />
          ) : (
            <Text variant="h1" my={3}>
              {title}
            </Text>
          ))}
        {!!widgets?.length && <WidgetManager widgets={widgets} headerInfo={{ compact }} />}
        {!!widgets?.length && !!contents.length && (
          <Text variant="h2" weight="medium" mb={1} mt={7}>
            همه محتواها
          </Text>
        )}
        <InfiniteScroll {...getInfiniteScrollProps()} loader={<ContentListLoading />}>
          <ContentList items={contents} />
        </InfiniteScroll>
      </Layout>
    </ThemeProvider>
  );
}

export async function getServerSideProps({ req, res, params: { params } }) {
  xIPCountry({ req, res });
  const [shortId] = params ? Array.from(params) : [];
  if (!shortId) {
    return { notFound: true };
  }

  try {
    const { data: category, meta } = await fetchCategory(shortId);
    if (!category?.id) {
      return { notFound: true };
    }
    return {
      props: { meta, category },
    };
  } catch (err) {
    Sentry.withScope((scope) => {
      scope.setTag('Status', err.response?.status);
      scope.setTag('StatusText', err.response?.statusText);
      scope.setTag('Url', err.response?.config?.url);
      Sentry.captureException(err);
    });

    return { notFound: true };
  }
}
